/**
 * @typedef {Object} AutoscrollService
 *
 * @property {function} activate
 * @property {function} performAutoscroll
 * @property {function} getScrollPositionY
 * @property {function} redefineLinkStateProcessor
 * @property {function} scrollTo
 * @property {function} scrollToElementById
 */


(function() {
    'use strict';

    angular.module( APP_ID )
        .factory( 'AutoscrollService', [ '$rootScope', '$uiRouterGlobals', '$uiRouter', '$transitions', '$location', '$window', '$document', '$timeout', 'LoggerFactory', AutoscrollService ]);

    /**
     * Provides a smart logic to determine vertical position to which should
     * the app autoscroll after a state transition (~page load).
     *
     * Autoscroll **MUST BE** activated (see method `activate()`) first
     * to evaluate information about what kind of routing has been peformed.
     * After that, the auto scrolling can be triggered by calling
     * `performAutoscroll()`.
     *
     * @param {ng.IRootScopeService} $rootScope
     * @param {uirouter.UiRouter} $uiRouter
     * @param {uirouter.UIRouterGlobals} $uiRouterGlobals
     * @param {uirouter.Transitions} $transitions
     * @param {ng.ILocationService} $location
     * @param {ng.IWindowService} $window
     * @param {ng.IDocumentService} $document
     * @param {ng.ITimeoutService} $timeout
     * @param {LoggerFactory} LoggerFactory
     *
     * @returns {AutoscrollService}
     */
    function AutoscrollService(
        $rootScope,
        $uiRouterGlobals,
        $uiRouter,
        $transitions,
        $location,
        $window,
        $document,
        $timeout,
        LoggerFactory
    ){


        /* ===== PRIVATE MEMBERS ============================================ */


        /** @type {Logger} */
        var logger = LoggerFactory.createLogger( 'AutoscrollService', false );

        /** @type {AutoscrollService} */
        var service = {

            // Methods
            activate:                   startRoutingListeners,
            getScrollPositionY:         getScrollPositionY,
            performAutoscroll:          performAutoscroll,

            scrollTo:                   scrollTo,
            scrollToElementById:        scrollToElementById,

            redefineLinkStateProcessor: redefineLinkStateProcessor
        };

        /** @const */
        var ROUTING_UPDATE_TYPE = {
            LOCATION_BROADCAST: '[1]location-broadcast',
            LOCATION_WATCH:     '[2]location-watch',
            STATE_TRANSITION:   '[3]state-transition'
        },

        ROUTING_STATUS = {
            STARTING:    'starting',
            IN_PROGRESS: 'progress',
            DONE:        'done',
            IGNORED:     'ignored' // To get IGNORED status, the Transition needs to be set as `{ notify:false }`
        },

        TOP_SCROLL_OFFSET           = 55,

        DEFAULT_SCROLLING_SPEED     = 500,
        DEFAULT_ELEMENT_TARGET_ID   = 'top';



        /**
         * Collects information about sequence in which the routing updates
         * were called. This sequence helps to determine what kind of
         * routing occured. Can contain any of actions defined in
         * {ROUTING_UPDATE_TYPE} enum.
         *
         * @type {string[]}
         */
        var routingUpdatesSequence = [];

        /**
         * Routing progress status. Can contain values defined
         * in {ROUTING_STATUS} enum.
         *
         * @type {string}
         */
        var routingStatus      = ROUTING_STATUS.STARTING;

        /**
         * Exposes last transition.
         *
         * @type {uirouter.Transition}
         */
        var currentTransition      = null;

        /**
         * HTML-based ID (without '#') of an element to which it should
         * be scrolled to. Can be defined
         *
         * @type {string}
         */
        var customScrollElementId  = DEFAULT_ELEMENT_TARGET_ID;


        /**
         * @type {number[]}
         */
        var scrollHistory;

        /**
         * MUST start with -1 to accommodate for initialisation phase.
         *
         * @type {number}
         */
        var scrollHistoryIndex;

        /**
         * True     - Browser's back button triggered the routing
         * False    - Browser's next button triggered the routing
         *
         * @type {boolean}
         */
        var wasBackButtonClicked = null;

        /**
         * Whether or not the service is in the first routing cycle (~ app is
         * routing for the first time).
         *
         * @type {boolean}
         */
        var isFirstRun = true;

        /**
         * Polymorphic function that generally performs additional
         * processing and then assigns proper Scrolling Resolver function
         * to {currentScrollingResolverFn}.
         *
         * *SHOULD BE* assigned when the routing event is identified and
         * executed right AFTER routing finishes.
         *
         * @type {function}
         */
        var currentScrollProcessorFn    = processNoScroll;


        /**
         * Scrolling resolver that retrieves vertical coordinates (in pixel units)
         * of the target scrolling position.
         *
         * *SHOULD BE* called as late as possible in the autoscrolling process,
         * ideally AFTER all the views and subviews are already rendered.
         *
         * @type {function}
         */
        var currentScrollingResolverFn  = null;



        ////////////////////////////////////////////////////////////////////////

        return service; // Expose API

        ////////////////////////////////////////////////////////////////////////





        /* ===== PUBLIC METHODS ============================================= */


        /**
         * Physically performs the autoscrolling based on the latest
         * given Y position resolver and routing type.
         *
         * This should be called by a special directive
         * (e.g. {autoscrollActivator} ) after DOM is rendered
         * to get most accurate results possible.
         *
         * @param {any} animationSpeedInMs
          */
        function performAutoscroll( animationSpeedInMs ) {
            $timeout( function () {
                var newScrollPositionY = getScrollPositionY();

                if ( newScrollPositionY !== null ) {
                    //DOM has finished rendering
                    scrollTo( newScrollPositionY, animationSpeedInMs );
                }
            }, 1 );
        }


        function scrollTo( positionY, animationSpeedInMs ){
            animationSpeedInMs = animationSpeedInMs || DEFAULT_SCROLLING_SPEED;
            angular.element( 'html,body' ).stop().animate({
                scrollTop: positionY
            }, animationSpeedInMs );
        }


        function scrollToElementById( elementId, animationSpeedInMs ){
            scrollTo( getElementsYPosition( elementId ), animationSpeedInMs );
        }


        /**
         * SHOULD BE called after the DOM is rendered to guarantee that the
         * scrolling position will be as accurate as possible.
         *
         * It also resets its inner members to default state.
         *
         * @returns {number} Number of vertical pixels the app should autoscroll to.
          */
        function getScrollPositionY() {
            logger.debug( 'Retrieving current scrolling resolver %s ...', currentScrollingResolverFn )
            return currentScrollingResolverFn();
        }


        /**
         * Allows to redefine the default element that should be autoscrolled
         * to in some scenarios.
         *
         * @param {function} callbackFn Callback accepting current transition {uirouter.Transition} as a parameter and returning HTML-based ID of the custom element.
         *
         */
        function redefineLinkStateProcessor( callbackFn ){
            customScrollElementId = callbackFn( currentTransition );
        }


        /**
         * Binds routing and transition listeners to intercept and process any
         * events that might occur.
         *
         * SHOULD BE run at the startup (in the Angular's module.run() method)
         * only ONCE to intercept all events.
         */
        function startRoutingListeners() {
            initScrollingHistory();

            // Initialise properties to allow for disallocation $onDestroy()
            var locationBroadcaster = $rootScope.$on( '$locationChangeSuccess', function() { // eslint-disable-line no-unused-vars

                // Check *MUST BE* here - Since Angular 1.6.2+, `$locationChangeSuccess` is being called even if `$location` hasn't changed!

                if ( $location.path() !== $rootScope.actualLocation ){
                    $rootScope.actualLocation = $location.path(); // Assign to detect 'back/next' button click
                    handleRoutingUpdate( ROUTING_UPDATE_TYPE.LOCATION_BROADCAST );
                }

            }),

            locationWatcher = $rootScope.$watch(function () { return $location.path() }, function( newLocation ) { // eslint-disable-line no-unused-vars
                checkPrevNextButtonUsage( newLocation );
                handleRoutingUpdate( ROUTING_UPDATE_TYPE.LOCATION_WATCH );
            });

            $transitions.onSuccess( { to: '**' }, function( $transition ){
                // Run *ONLY ONCE* (will be triggered after $view is loaded & DOM construction is finished!)
                runAfterRoutingFinishes();

                /** Notification is turned off -> skip */
                if ( !$transition._options.notify ){
                    routingStatus = ROUTING_STATUS.IGNORED;
                }

                currentTransition = $transition;
                handleRoutingUpdate( ROUTING_UPDATE_TYPE.STATE_TRANSITION );
            });
        }





        /* ===== PRIVATE FUNCTIONS ========================================== */


        /**
         * Initialises the scrolling history and page's position from the
         * browser's History State (if there is any). If there is no History State,
         * it initialises an empty history and page's position.
         */
        function initScrollingHistory() {
            logger.debug( "Initialising history...", $window.history );
            // Recover scrolling position(s) from the STATE history (if exists) ...
            if ( $window.history.state && $window.history.state.hasOwnProperty( 'position' )) {
                scrollHistoryIndex  = $window.history.state.position;
                scrollHistory       = $window.history.state.scrollHistory;
                logger.debug( "State history DOES EXIST!", scrollHistoryIndex, scrollHistory );
            // ... or init a brand new one.
            } else {
                scrollHistoryIndex  = 0;
                scrollHistory       = []
                logger.debug( "State history does NOT exist - initialising empty one!", scrollHistoryIndex, scrollHistory );
            }
        }


        /**
         * Function that *MUST BE* executed only ONCE after the initial routing
         * finishes. Executes `currentScrollProcessorFn` (that will prepare a scrolling
         * Resolver function for a later scrolling action), saves history counter and
         * vertical coordinates into browser's history and reset's the finishes the
         * routing cycle by resetting the service into its original state.
         *
         * @param {function} callbackFn
         */
        function runAfterRoutingFinishes() {
            // Runs the function after the DOM has finished its initialisation
            $timeout(function () {
                logger.debug( 'Processing collected routing information, updating history...!' );

                // **Order of the calls super-important!**

                /** Run *ONLY IF* it's not a first run and it's not ignored. */
                if ( routingUpdatesSequence.length !== 0 && !isFirstRun && !isPrevNextStateChange() ) {
                    scrollHistoryIndex++;
                }

                currentScrollProcessorFn();
                updateHistoryState();

                resetToDefaults();
                isFirstRun = false;
            }, 0 );
        }


        /**
         * Evaluates what kind of browser button has been used (if any) and
         * assigns it into the {wasBackButtonClicked}.
         *
         * @param {Object} newLocation
         */
        function checkPrevNextButtonUsage( newLocation ) {
            //true only for onPopState
            if( $rootScope.actualLocation === newLocation ) {
                var historyState = $window.history.state;
                wasBackButtonClicked = !!(historyState && historyState.position < scrollHistoryIndex );
            }
        }


        /**
         * Returns default vertical autoscroll Y position in pixels.
         *
         * @returns {number}
         */
        function getDefaultScrollYPosition() {
            return 0;
        }


        /**
         * Does not autoscroll at all and leaves it to browser's
         * default.
         *
         * @returns {null}
         */
        function getNoScrollResolver() {
            return null;
        }


        /**
         * Retrieves the Scroll Y position in pixels from a scrolling
         * history, which can be useful when traversing back and forth
         * via back/next buttons.
         *
         * @returns {number}
          */
        function getScrollYPositionFromHistory() {
            logger.debug( 'Returning Y position from scrolling history - Index: %s | Value: %s', scrollHistoryIndex, scrollHistory[ scrollHistoryIndex ] );
            return scrollHistory[ scrollHistoryIndex ];
        }


        /** Default processor - assigns default scrolling Resolver and
         * *SHOULD BE* called in case scrolling is ignored or not detected. */
        function processNoScroll() {
            logger.debug( 'No scrolling - default browser behaviour' );
            currentScrollingResolverFn = getNoScrollResolver;
        }


        /**
         * When 'go back' or 'go forward' browser buttons are
         * triggered, the service retrieves the scrolling position
         * from its history (if any history exists).
         *
         * @returns {function}
         */
        function processPrevNextStateChange() {
            if ( wasBackButtonClicked ) {
                // Back button
                logger.debug( "Routing type: BACK button used!" );

                scrollHistoryIndex--;
                currentScrollingResolverFn = getScrollYPositionFromHistory; // TODO
            } else {
                // Next button
                logger.debug( "Routing type: NEXT button used!" );

                scrollHistoryIndex++;
                currentScrollingResolverFn = getScrollYPositionFromHistory;
            }
        }


        /**
         * Retrieves Y position resolver for a default scrolling
         * position when an entire page loads for the first time
         * (e.g. not a tab, dialog or expanding panel is loaded).
         *
         * @returns {function}
          */
        function processPageStart() {
            logger.debug( "Routing type: PAGE START!" );

            // Default behaviour
            currentScrollingResolverFn = getDefaultScrollYPosition;
        }


        /**
         * Gets an autoscrolling position Y resolver for
         * a situation when application was initialised
         * for the first time.
         *
         * @returns {function}
         */
        function processAppStart() {
            logger.debug( "Routing type: APP START!" );

            // No scrolling
            currentScrollingResolverFn = getNoScrollResolver;
        }


        /**
         * Simple helper function checking wether or not the 'go back'
         * or 'go forward' browser button was clicked. */
        function isPrevNextStateChange() {
            return ROUTING_UPDATE_TYPE.LOCATION_BROADCAST === routingUpdatesSequence[0];
        }


        /**
         * Gets an autoscrolling position Y resolver for
         * a situation where application state changed
         * through a standard link click.
         *
         * This behaviour can be optionally overriden via callback
         * function returning HTML-based ID passed as a parameter
         * to the {redefineLinkStateProcessor} public method.
         *
         * @returns {function}
         */
        function processLinkStateChange() {
            logger.debug( "Routing type: STANDARD LINK!" );

            var customElementId     = customScrollElementId;
            customScrollElementId   = DEFAULT_ELEMENT_TARGET_ID;

            currentScrollingResolverFn = function() {
                return getElementsYPosition( customElementId );
            }
        }


        /**
         * Retrieves a vertical Y position in pixels for given URL anchor.
         *
         * @param {string} An anchor, defined by HTML-based ID of an element (without '#' character).
         *
         * @returns {function}
          */
        function processAnchorRouting() {
            logger.debug( "ANCHOR ROUTING!", '#' + $uiRouterGlobals.params[ '#' ] );

            currentScrollingResolverFn = function(){
                return getElementsYPosition( $uiRouterGlobals.params[ '#' ] );
            }
        }


        /**
         * Logs given routing update efficiently into sequence.
         *
         * Current queue limit is 3 updates - that should be enough
         * to determine what kind of routing event happened.
         *
         * @param {ROUTING_UPDATE_TYPE} updateType
         *
         * @returns {boolean} Whether the routing update sequence is full or not.
          */
        function handleRoutingUpdate( updateType ) {

            if ( routingStatus === ROUTING_STATUS.STARTING || routingStatus === ROUTING_STATUS.IGNORED ) {
                logger.debug( 'Routing STARTED!' );
            }

            // Prioritised order of performing checks
            switch ( routingStatus ) {
                case ROUTING_STATUS.IGNORED: {
                    logger.debug( 'Routing IGNORED - Marking it as `DONE` and skipping it...')

                    // Ignored = Do no action.
                    routingStatus = ROUTING_STATUS.DONE;
                };
                case ROUTING_STATUS.DONE: {
                    logger.debug( 'Routing DONE - Skipping handling of #%s ...', updateType );
                    return false; // BREAK!
                }
                case ROUTING_STATUS.STARTING: {
                    if ( !isFirstRun ) recordPreviousPageScrollingPosition();

                    // First routing update = start of new cycle.
                    routingStatus = ROUTING_STATUS.IN_PROGRESS;
                };
                case ROUTING_STATUS.IN_PROGRESS: {
                    // Log the routing update
                    routingUpdatesSequence.push( updateType );
                    logger.debug( '%s.) Adding routing update `%s`.', routingUpdatesSequence.length, updateType );

                    // If the evaluation is `true`, no need to log more routing events
                    var processorFn = identifyRoutingAndGetProcessor();

                    if ( processorFn ) {
                        currentScrollProcessorFn = processorFn;
                        routingStatus = ROUTING_STATUS.DONE;
                        logger.debug( 'Routing FINISHED!' );
                    }

                    return true; // BREAK!
                }
            }
        }


        /**
         * Analyses the collected routing information, tries to detect
         * what kind of a routing event has occured and returns event-specific
         * autoscroll processing function (a function that will get processed
         * AFTER the routing is finished and assigns proper resolver function
         * into the `currentScrollingResolverFn`).
         *
         * @returns {(null|function)}
         */
        function identifyRoutingAndGetProcessor() {
            // Can happen during the first run
            if ( isPrevNextStateChange() ) {
                return processPrevNextStateChange;
            } else if ( $uiRouterGlobals.params && $uiRouterGlobals.params[ '#' ] ) {
                return processAnchorRouting;
            } else if ( currentTransition && currentTransition.$from().name == '' ) {
                return processAppStart;
            } else if ( isTransitioningBetweenCategories() ){
                // If transitioning between 2 different page categories
                return processPageStart;
            } else if ( currentTransition && ROUTING_UPDATE_TYPE.STATE_TRANSITION === routingUpdatesSequence[0] ) {
                return processLinkStateChange;
            } else {
                logger.debug( 'Not enough routing information - cannot get any processor!' )
            }

            return null;
        }


        /**
         * Whether or not the page is transitioning from one category to
         * another (e.g. study areas -> postgraduate programmes).
         *
         * @returns {boolean}
         */
        function isTransitioningBetweenCategories() {
            return currentTransition && currentTransition._treeChanges.from.length > 2 && currentTransition._treeChanges.to.length > 2 && currentTransition._treeChanges.from[ 2 ].state.name !== currentTransition._treeChanges.to[ 2 ].state.name;
        }


        /**
         * Returns all inner members to its default values.
         *
         * *SHOULD* be on cycle's end (~ after is finished, evaluated
         * and scroll resolver function prepared to be executed by the
         * frontend).
         */
        function resetToDefaults() {
            logger.debug( 'Reseting autoscroller to defaults.' );
            routingStatus               = ROUTING_STATUS.STARTING;
            currentTransition           = null;
            routingUpdatesSequence      = [];
            currentScrollProcessorFn    = processNoScroll;
        }


        /**
         * Retrieves a vertical Y position in pixels for an element
         * specified by its HTML-based ID (without '#' character),
         * or, if element does not exist, reverts to the element
         * with ID defined by {customScrollElementId}.
         *
         * @param {string} elementId
         *
         * @returns {number}
          */
        function getElementsYPosition( elementId ) {
            var domElement = angular.element( '#' + elementId );

            // If the given element does not exist, try to use custom one
            if ( !domElement.length ){
                // If the custom element does not exist, try revert to default position
                domElement = angular.element( '#' + customScrollElementId );

                if ( !domElement.length ) { return getDefaultScrollYPosition(); }
            }

            var rect = domElement[ 0 ].getBoundingClientRect(),
            scrollTop = window.pageYOffset || document.documentElement.scrollTop;

            return rect.top + scrollTop - TOP_SCROLL_OFFSET;
        }


        /**
         * *SHOULD BE* triggered BEFORE the routing starts to record
         * scroll position on the page that's being left (the page that
         * triggered the routing action).
         */
        function recordPreviousPageScrollingPosition() {
            // Necessary to make the auto-scrolling cross-browser
            if ( scrollHistoryIndex < scrollHistory.length ) {
                scrollHistory[ scrollHistoryIndex ] = ( $window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0 ); // eslint-disable-line angular/document-service
            } else {
                scrollHistory.push( $window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0 ); // eslint-disable-line angular/document-service
            }

            logger.debug( 'Recording scrolling position - Page no.: %s | Y: %s', scrollHistoryIndex, scrollHistory[ scrollHistoryIndex ], scrollHistory );
        }


        /**
         * Saves the scrolling history into the browser's History State Object.
         *
         * This *SHOULD* happen AFTER the processing function is called
         * and BEFORE the cycle is re-set into defaults.
         */
        function updateHistoryState(){
            $window.history.replaceState({
                position:       scrollHistoryIndex,
                scrollHistory:  scrollHistory
            }, '', $window.location.href ); // *MUST* include current URL due to Safari's security protocols!
        }
    };
})();
